import type { ConnectHttpClient, ConnectRequestOptionsArgs, ConnectError, HttpResponseData } from '@ucc/common'
import { HttpResponseError } from '@ucc/common'
import type { Observable } from 'rxjs'
import { from } from 'rxjs'
import { authenticatedFetch } from './auth'
import type { TypedResponse } from './auth/authenticatedFetch'

interface FetchParameters {
  readonly method?: string
  readonly body?: string
  readonly options?: ConnectRequestOptionsArgs
}

export class FetchHttpClient implements ConnectHttpClient {
  get<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.getPromise<T, E>(url, options))
  }

  getPromise<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { options, method: 'GET' })
  }

  head<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.headPromise<T, E>(url, options))
  }

  headPromise<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { method: 'HEAD', options })
  }

  post<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.postPromise<T, E>(url, body, options))
  }

  postPromise<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { method: 'POST', body, options })
  }

  put<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.putPromise<T, E>(url, body, options))
  }

  putPromise<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { method: 'PUT', body, options })
  }

  patch<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.patchPromise<T, E>(url, body, options))
  }

  patchPromise<T = unknown, E = ConnectError>(
    url: string,
    body?: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { method: 'PATCH', body, options })
  }

  delete<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Observable<HttpResponseData<T, E>> {
    return from(this.deletePromise<T, E>(url, options))
  }

  deletePromise<T = unknown, E = ConnectError>(
    url: string,
    options?: ConnectRequestOptionsArgs,
  ): Promise<HttpResponseData<T, E>> {
    return this.fetchRequest(url, { method: 'DELETE', options })
  }

  private async fetchRequest<T = unknown, E = ConnectError>(
    request: RequestInfo,
    params?: FetchParameters,
  ): Promise<HttpResponseData<T, E>> {
    /* istanbul ignore next not useful for unit test */
    let body = params?.body
    if (typeof body === 'object') {
      body = JSON.stringify(body)
    }

    /* istanbul ignore next */
    const init = {
      method: params?.method ?? 'GET',
      headers: new Headers(params?.options?.headers),
      body,
      ...params?.options,
    }
    init.headers.set('Content-Type', 'application/json')

    let promise: Promise<Response | TypedResponse<unknown>>
    /* istanbul ignore next not useful for unit test */
    if (params?.options?.allowUnauthenticated) {
      promise = fetch(request, init)
    } else {
      promise = authenticatedFetch(request, init)
    }

    const response = await promise
    const { headers, status, statusText } = response
    if (status >= 400 && status <= 500) {
      throw new HttpResponseError(`${status}: ${statusText}`, undefined, status, statusText)
    }

    /**
     * Since some requests return void or non-json values, we need to convert the data to text.
     * Verify that the data can be parsed to json and then set the data on the response.
     */
    const res = await response.text()
    let data
    try {
      if (response.headers.get('Content-Type')?.startsWith('application/json')) {
        /* istanbul ignore next */
        data = JSON.parse(res)
      }
    } catch (err) {
      /* istanbul ignore next */
      data = err
    }
    return { headers, status, statusText, data }
  }
}
